import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/ArrowCounterClockwise";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { notifyRefetch } from "@freightsimple/api-runtime/refetchRegistry";
import { showApiError } from "@freightsimple/app-shell/api/showApiError";
import { useCompanyWebhooksApi } from "@freightsimple/hubble-api/hooks";
import { CompanyWebhookDeliveryState } from "@freightsimple/generated-hubble-openapi-client/models/CompanyWebhookDeliveryState";
import type { CompanyWebhookDelivery } from "@freightsimple/generated-hubble-openapi-client/models/CompanyWebhookDelivery";
import { toast } from "sonner";

interface WebhookDeliveryRedeliverButtonProps {
  delivery: CompanyWebhookDelivery;
  /** Another send of this event already reached the endpoint, so this would be a second copy. */
  alreadyReachedEndpoint?: boolean | undefined;
}

// The rest (failed, gave up, replaced) can be sent again; the server has the last word.
function notRedeliverableReason(
  delivery: CompanyWebhookDelivery,
): string | undefined {
  if (delivery.state === CompanyWebhookDeliveryState.Delivered) {
    return "Already delivered";
  }
  if (delivery.state === CompanyWebhookDeliveryState.Pending) {
    return "Still being retried on its schedule";
  }
  return undefined;
}

/** The original send of the row's event: every redelivery points back at it. */
export function webhookDeliveryRootId(
  delivery: CompanyWebhookDelivery,
): string {
  return delivery.retriedFromDeliveryId ?? delivery.companyWebhookDeliveryId!;
}

export function webhookDeliveryRedeliverConfirm(
  delivery: CompanyWebhookDelivery,
  alreadyReachedEndpoint: boolean,
): string {
  // A redelivery goes to the webhook's URL as it is now, not the one on this row.
  const base =
    "It goes to the webhook's current URL again, with the same event id.";
  // Allowed for ops on purpose: a customer that received the event and lost it asks for it again,
  // and dedupes on the event id. Only the customer is refused this on the Dashboard.
  if (alreadyReachedEndpoint) {
    return `A send of this event already reached the endpoint, so this sends it a second time. ${base}`;
  }
  if (delivery.state === CompanyWebhookDeliveryState.Superseded) {
    return `A newer update for this shipment already went out, so this sends the older state again. ${base}`;
  }
  return base;
}

/**
 * Sends a delivery's event to the customer's endpoint again, as ops: no cap, the same event id,
 * a fresh signature, and the operator's email on the new row. An event another send already delivered
 * can still go again, after a warning. The server refuses a paused webhook and an expired delivery
 * with a sentence that says what to do, which the error toast shows.
 */
export function WebhookDeliveryRedeliverButton({
  delivery,
  alreadyReachedEndpoint = false,
}: WebhookDeliveryRedeliverButtonProps) {
  const api = useCompanyWebhooksApi();
  const reason = notRedeliverableReason(delivery);

  async function redeliver(): Promise<void> {
    try {
      const sent = await api.redeliverWebhookDelivery({
        redeliverWebhookDeliveryRequest: {
          companyId: delivery.companyId!,
          deliveryId: delivery.companyWebhookDeliveryId!,
        },
      });
      if (sent.state === CompanyWebhookDeliveryState.Delivered) {
        toast.success("Redelivered");
      } else if (sent.state === CompanyWebhookDeliveryState.Pending) {
        toast.warning(
          "The endpoint refused it again. We'll keep retrying on the usual schedule.",
        );
      } else {
        // Stopped without a retry, e.g. an endpoint this environment may not call.
        toast.error(sent.errorMessage ?? "It was not sent.");
      }
    } catch (e) {
      await showApiError(e, "Redeliver");
      // A refusal is not a mutation, so nothing reloads on its own: the row would keep offering
      // an action the server just turned down for a reason that is now out of date on screen.
      notifyRefetch(api);
    }
  }

  const props = {
    icon: ArrowCounterClockwiseIcon,
    tooltip: "Send this event to the endpoint again",
    children: "Redeliver",
  };
  return reason !== undefined ? (
    <Button {...props} disabled disabledMessage={reason} />
  ) : (
    <Button
      {...props}
      confirm={{
        title: "Redeliver this event?",
        message: webhookDeliveryRedeliverConfirm(
          delivery,
          alreadyReachedEndpoint,
        ),
      }}
      onClick={redeliver}
    />
  );
}
